import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import { FiSearch } from "react-icons/fi";
import AddressPopUp from "./AddressPopUp";
import { formatCurrency, formatDate, formatNumber } from "../../utils/helper";
import YearFilterDropdown from "../team-managment/YearFilterDropdown";

const containerStyle = {
  width: "100%",
  height: "80vh",
};

const libraries = ["places"];

export default function PropertiesTab(props) {
  const currentYear = new Date().getFullYear();

  // ✅ selected year state
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);

  // ✅ last 5 years list
  const years = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, [currentYear]);

  // Filter properties by createdat year = currentYear
  const filtered = useMemo(() => {
    return props.deals.filter((p) => {
      try {
        if (!p.closingdate) return false;
        const y = new Date(p.closingdate).getFullYear();
        return y === selectedYear;
      } catch (e) {
        return false;
      }
    });
  }, [props.deals, selectedYear]);

  // Map load
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyClMaFJLo3buNYK-siytTa6AWD_72TgrE8",
    libraries,
  });

  const mapRef = useRef(null);
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);
  const panTo = useCallback((lat, lng, zoom = 15) => {
    if (!mapRef.current) return;
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(zoom);
  }, []);

  const defaultCenter = { lat: 39.8283, lng: -98.5795 }; // continental USA
  const defaultZoom = 5;

  const center = useMemo(() => {
    // Agar aap chahte ho filtered ke first property pe na jaye
    return defaultCenter;
  }, [filtered]);

  // Hover & selected state
  const [hoveredId, setHoveredId] = useState(null);
  const [selected, setSelected] = useState(null);

  // Search dropdown
  const [query, setQuery] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const addresses = filtered.filter((p) =>
    p.propertyaddress?.toLowerCase().includes(query.trim().toLowerCase()),
  );

  // marker handlers
  const handleMarkerMouseOver = (prop) => {
    setHoveredId(prop.id);
    setSelected(prop);
  };
  const handleMarkerMouseOut = () => {
    // keep selected open if clicked; on mouseout close only preview
    setHoveredId(null);
    // setSelected(null); // we want hover to show, but clicking will also select
  };

  const handleAddressSelect = (prop) => {
    if (!prop || !prop.propertyLocation) return;
    setQuery(prop.propertyaddress);
    setDropdownVisible(false);
    setSelected(prop);
    panTo(prop.propertyLocation.lat, prop.propertyLocation.lng, 16);
    setTimeout(() => {
      setHoveredId(prop.id); // ensure popup shows
    }, 200);
  };

  const animateZoomAndPan = (lat, lng, targetZoom = 16) => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const currentZoom = map.getZoom();

    // Smooth pan
    map.panTo({ lat, lng });

    // Smooth zoom
    const steps = 10;
    const zoomStep = (targetZoom - currentZoom) / steps;
    let count = 0;

    const zoomInterval = setInterval(() => {
      map.setZoom(map.getZoom() + zoomStep);
      count++;
      if (count >= steps) {
        clearInterval(zoomInterval);
        map.setZoom(targetZoom); // final zoom
      }
    }, 40);
  };

  const resetMap = () => {
    setSelected(null);
    setHoveredId(null);
    setDropdownVisible(false);

    // reset map center + zoom exactly like first load
    if (mapRef.current) {
      mapRef.current.panTo(center);
      mapRef.current.setZoom(3);
    }
  };

  if (loadError) return <div>Map load error</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div className="w-full relative">
      {/* Search bar */}
      {/* Year Filter Dropdown */}
      {/* <div className="mb-2">
        <select
          value={selectedYear}
          onChange={(e) => {
            setSelectedYear(Number(e.target.value));
            resetMap(); // year change pe map reset
          }}
          className="w-full px-3 py-2 rounded-md shadow bg-white text-sm outline-none"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div> */}
      <div className="flex justify-end mb-6">
      <button
        onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
        className="border border-gray-300 rounded-md px-5 py-1 text-sm w-full sm:w-auto text-left"
      >
        {selectedYear}
      </button>
      <YearFilterDropdown
        visible={yearDropdownVisible}
        onSelect={(year) => {
          setSelectedYear(year);
          setYearDropdownVisible(false);
        }}
        activateOutsideClick={() => setYearDropdownVisible(false)}
        style={{ top: "40px", right: 0 }}
      />
      </div>

      <div className="absolute z-40 top-16 left-4">
        <div className="relative w-[320px]">
          <div className="flex items-center bg-white rounded-lg shadow px-3 py-2 w-full">
            <FiSearch size={18} className="mr-2 text-gray-500" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setDropdownVisible(true);
              }}
              onFocus={() => setDropdownVisible(true)}
              placeholder="Search property address..."
              className="outline-none w-full text-sm"
            />
            <button
              onClick={() => {
                setQuery("");
                resetMap(); // <-- added
              }}
              className="ml-2 text-xs text-gray-500"
            >
              Clear
            </button>
          </div>

          {/* Dropdown popup using your style */}
          <AddressPopUp
            visible={dropdownVisible}
            items={addresses}
            onSelect={(item) => handleAddressSelect(item)}
            onClickOutside={() => setDropdownVisible(false)}
            style={{ left: 0 }}
          />
        </div>

        {/* small info about count */}
        <div className="mt-2 bg-white rounded-md p-2 shadow text-sm">
          Showing {filtered.length} properties for year {selectedYear}
        </div>
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={defaultZoom}
        onLoad={onLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {filtered.map((prop) => {
          const pos = prop.propertyLocation;
          if (!pos) return null;
          return (
            <Marker
              key={prop.id}
              position={{ lat: pos.lat, lng: pos.lng }}
              onMouseOver={() => handleMarkerMouseOver(prop)}
              onMouseOut={() => handleMarkerMouseOut()}
              onClick={() => {
                if (selected?.id === prop.id) {
                  // Already selected → Zoom out to original
                  setSelected(null);
                  setHoveredId(null);
                  animateZoomAndPan(center.lat, center.lng, 3); // reset map
                } else {
                  // Select marker → zoom in
                  setSelected(prop);
                  animateZoomAndPan(pos.lat, pos.lng, 16);
                }
              }}
            />
          );
        })}

        {/* InfoWindow on hover/selected */}
        {selected &&
          selected.propertyLocation &&
          (hoveredId === selected.id || true) && (
            <InfoWindow
              position={{
                lat: selected.propertyLocation.lat + 0.0007,
                lng: selected.propertyLocation.lng,
              }}
              onCloseClick={() => {
                setSelected(null);
                setHoveredId(null);
              }}
            >
              <div
                style={{
                  maxWidth: 260,
                  padding: "10px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                }}
              >
                <div className="w-full">
                  {selected.propertyphoto &&
                  selected.propertyphoto.length > 0 ? (
                    <img
                      src={selected.propertyphoto[0]}
                      alt="prop"
                      className="w-full h-28 object-cover rounded"
                    />
                  ) : (
                    <img
                      src="/assets/broken.png"
                      alt="prop"
                      className="w-full h-28 object-cover rounded"
                    />
                  )}
                </div>
                <div className="mt-2">
                  <h4 className="font-semibold text-sm">
                    {selected.propertyaddress || "Address not available"}
                  </h4>
                  <p className="text-sm font-medium mt-1">
                    Type:{" "}
                    {selected.propertyType ? `${selected.propertyType}` : "N/A"}
                  </p>
                  <p className="text-sm font-medium">
                    Price:{" "}
                    {selected.offerPrice
                      ? `${formatCurrency(selected.offerPrice)}`
                      : "N/A"}
                  </p>

                  <p className="text-sm font-medium ">
                    Closing Date: {formatDate(selected.closingdate) || "N/A"}
                  </p>
                </div>
              </div>
            </InfoWindow>
          )}
      </GoogleMap>
    </div>
  );
}
